import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useAddClient, useEditClient } from "../../queries/admin";
import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { DocumentIcon } from "@heroicons/react/24/outline";
import { queryClient } from "../../queries/client";
import { ClientModel } from "../../types/user";
import { useAuthStore } from "../../store/useAuthStore";
import { decodeAuthToken } from "../../utils/auth";

const initialClientValues: ClientModel = {
  name: "",
  logo: "",
  address: "",
  email: "",
  phone: "",
  website: "",
};

export default function AddCompanyModal({
  isOpen,
  onClose,
  clientData,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientData?: ClientModel;
}) {
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const [clientModel, setClientModel] =
    useState<ClientModel>(initialClientValues);
  const [isDragging, setIsDragging] = useState(false);
  const { setAuth } = useAuthStore();
  const { mutate: addClient } = useAddClient();
  const { mutate: editClient } = useEditClient();

  const readFileAsDataUrl = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setClientModel((prev) => ({
          ...prev,
          logo: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    },
    [setClientModel]
  );

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) readFileAsDataUrl(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) readFileAsDataUrl(file);
    },
    [readFileAsDataUrl]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveLogo = () => {
    setClientModel((prev) => ({ ...prev, logo: "" }));
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const handleButtonClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const isEditMode = !!clientData;

  return (
    <Transition show={isOpen}>
      <Dialog className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <>
                  <div className="space-y-2">
                    <div className="border-b border-gray-900/10 pb-2 flex flex-row items-center justify-between">
                      <h2 className="text-base font-semibold leading-7 text-gray-900">
                        {isEditMode ? "Edit Client" : "Add Client"}
                      </h2>
                      <XMarkIcon
                        className="w-6 cursor-pointer h-6"
                        onClick={() => {
                          onClose();
                        }}
                      />
                    </div>

                    <div className="border-b border-gray-900/10">
                      <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-4">
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Company Name
                          </label>
                          <div className="mt-2">
                            <input
                              value={clientModel.name}
                              id="name"
                              name="name"
                              type="name"
                              autoComplete="off"
                              onChange={(e) => {
                                setClientModel({
                                  ...clientModel,
                                  name: e.target.value,
                                });
                              }}
                              className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>
                        {/* <div className="flex flex-row sm:col-span-2"> */}
                        <div className="sm:col-span-3">
                          <label
                            htmlFor="email"
                            className="block text-sm px-2 font-medium leading-6 text-gray-900"
                          >
                            Email address
                          </label>
                          <div className="mt-2">
                            <input
                              value={clientModel.email}
                              id="email"
                              name="email"
                              type="email"
                              onChange={(e) => {
                                setClientModel({
                                  ...clientModel,
                                  email: e.target.value,
                                });
                              }}
                              autoComplete="email"
                              className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>

                        <div className="sm:col-span-3">
                          <label
                            htmlFor="contactnumber"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Contact Number
                          </label>
                          <div className="mt-2">
                            <input
                              value={clientModel.phone}
                              id="contactnumber"
                              name="contactnumber"
                              type="tel"
                              inputMode="tel"
                              onChange={(e) => {
                                setClientModel({
                                  ...clientModel,
                                  phone: e.target.value,
                                });
                              }}
                              autoComplete="off"
                              className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>
                        {/* </div> */}

                        <div className="sm:col-span-4">
                          <label
                            htmlFor="website"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Company Website
                          </label>
                          <div className="mt-2">
                            <input
                              value={clientModel.website}
                              id="website"
                              onChange={(e) => {
                                setClientModel({
                                  ...clientModel,
                                  website: e.target.value,
                                });
                              }}
                              name="website"
                              type="website"
                              autoComplete="website"
                              className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>
                        <div className="col-span-full">
                          <label
                            htmlFor="about"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Address
                          </label>
                          <div className="mt-2">
                            <textarea
                              id="address"
                              name="address"
                              onChange={(e) => {
                                setClientModel({
                                  ...clientModel,
                                  address: e.target.value,
                                });
                              }}
                              rows={3}
                              className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              defaultValue={""}
                            />
                          </div>
                        </div>
                        <div className="col-span-full">
                          <label
                            htmlFor="logo"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Company Logo
                          </label>
                          <div className="mt-2 w-full">
                            <input
                              ref={inputFileRef}
                              className="hidden"
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                            />
                            {clientModel.logo ? (
                              <div className="relative w-full rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                                <img
                                  className="w-full h-48 object-cover"
                                  src={clientModel.logo}
                                  alt="client-logo"
                                />
                                <div className="absolute top-2 right-2 flex gap-2">
                                  <button
                                    type="button"
                                    onClick={handleButtonClick}
                                    className="rounded-md bg-white dark:bg-gray-700 cursor-pointer px-2 py-1 text-xs font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                                  >
                                    Change
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleRemoveLogo}
                                    className="rounded-md bg-white dark:bg-gray-700 cursor-pointer p-1 text-gray-500 dark:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:text-red-500"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={handleButtonClick}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                className={`w-full rounded-lg border border-gray-200 dark:border-gray-600 flex flex-col items-center justify-center cursor-pointer transition-colors py-8 ${
                                  isDragging
                                    ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
                                    : "hover:border-gray-300 dark:hover:border-gray-500"
                                }`}
                              >
                                <DocumentIcon className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-3" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Drag and drop or{" "}
                                  <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                                    choose file(s)
                                  </span>{" "}
                                  to upload
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-x-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-sm cursor-pointer font-semibold leading-6 text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      onClick={(e) => {
                        e.preventDefault();
                        if (isEditMode) {
                          editClient(clientModel, {
                            onSuccess: (data: any) => {
                              onClose();
                              queryClient.invalidateQueries({
                                queryKey: ["clients", "all"],
                              });
                              queryClient.invalidateQueries({
                                queryKey: ["clients", "detail", clientModel.id],
                              });
                              toast("Client Edited Successfully", {
                                type: "success",
                                autoClose: 2000,
                              });
                            },
                            onError: (data: any) => {
                              toast(data?.error ?? "Something went wrong!", {
                                type: "error",
                                autoClose: 5000,
                              });
                            },
                          });
                        } else {
                          addClient(clientModel, {
                            onSuccess: (data: any) => {
                              const responseData = data?.data ?? data;
                              const token = responseData?.token;
                              const newClientId = responseData?.id;
                              // Keep the "Bearer " prefix — request interceptor
                              // sends accessToken verbatim and the backend extracts
                              // the JWT via accessToken.split(" ")[1].
                              if (token) {
                                // The creator becomes ADMIN of the new org; the
                                // fresh token carries that role. Decode it so the
                                // UI adopts the new role/clientId instead of
                                // keeping the stale (e.g. USER) role.
                                const decoded = decodeAuthToken(token);
                                setAuth({
                                  accessToken: token,
                                  clientId:
                                    newClientId ?? decoded?.clientId ?? undefined,
                                  ...(decoded
                                    ? {
                                        role: decoded.role,
                                        email: decoded.email,
                                        id: decoded.userId,
                                      }
                                    : {}),
                                });
                              }
                              onClose();
                              queryClient.invalidateQueries({
                                queryKey: ["clients"],
                              });
                              queryClient.invalidateQueries({
                                queryKey: ["users", "available-clients"],
                              });
                              toast("Organization Created Successfully", {
                                type: "success",
                                autoClose: 2000,
                              });
                              window.location.reload();
                            },
                            onError: (data: any) => {
                              toast(data?.error ?? "Something went wrong!", {
                                type: "error",
                                autoClose: 5000,
                              });
                            },
                          });
                        }
                      }}
                      className="rounded-md cursor-pointer bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                      Save
                    </button>
                  </div>
                </>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
